import { _ } from "../polyglot/polyglot.js";
import {
	circleCheckIcon,
	exclamationTriangleIcon,
} from "../elements/icons/index.js";
import { eventManager } from "@foretagsplatsen/yaem";
import { iconAndText } from "../elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * Widget displaying a notification when an automatic save event is triggered,
 * and when it succeeds.
 *
 * The widget works by listening to 3 global events:
 *
 * - autoSave.schedule: Schedule an auto-save, with a function to actually do
 *   the work.
 * - autoSave.success: Notify the user that the auto-save was successful.
 * - autoSave.failure: Notify the user that the auto-save failed.
 *
 * As soon as an autosave is scheduled, the loading animation is started, to
 * inform the user that an action is being done in the background.
 *
 * When the auto save succeeds, the loading animation is stopped, and a "Saved"
 * notification is displayed for a few seconds. If it fails, a failure
 * notification is displayed.
 */
export default class AutoSaveWidget extends Widget {
	constructor() {
		super(...arguments);

		this._isOngoing = false;
		this._lastSaveSucceeded = false;

		eventManager
			.at("autoSave")
			.register("schedule", (func) => this._scheduleAutoSave(func));

		eventManager
			.at("autoSave")
			.register("success", () => this._notifyAutoSaveSucceeded());

		eventManager
			.at("autoSave")
			.register("failure", () => this._notifyAutoSaveFailed());
	}

	renderContentOn(html) {
		html.div({ id: "auto-save" }, this._renderStatusOn.bind(this));
	}

	_renderStatusOn(html) {
		html.div(
			{ class: "status" },
			this._lastSaveSucceeded
				? iconAndText(
						{ icon: circleCheckIcon, reversed: true },
						_("Saved"),
					)
				: iconAndText(
						{ icon: exclamationTriangleIcon },
						_("Save failed"),
					),
		);
	}

	/**
	 * Set a timer to trigger an auto-save in 3 seconds.
	 *
	 * If a timer is already in place, cancel it first, so that we do not
	 * repeatedly trigger saves.
	 *
	 * @param {function} func - Function to evaluate to perform the save.
	 */
	_scheduleAutoSave(func) {
		if (this._isOngoing) {
			return;
		}

		// Trigger the loading widget if it was not already displayed.

		eventManager.at("sync").trigger("loading");
		this._isOngoing = true;

		if (this._autoSaveTimer) {
			clearTimeout(this._autoSaveTimer);
			this._autoSaveTimer = null;
		}

		this._autoSaveTimer = setTimeout(() => {
			func();
			this._isOngoing = false;
			// If there's no other ongoing request, this hide the loading widget.
			eventManager.at("sync").trigger("loaded");
		}, 3000);
	}

	_notifyAutoSaveSucceeded() {
		this._updateStatus(true);
	}

	_notifyAutoSaveFailed() {
		this._updateStatus(false);
	}

	_updateStatus(success) {
		this._lastSaveSucceeded = success;
		this.update();
		this._blinkStatus();
	}

	_blinkStatus() {
		const element = this.asJQuery().find(".status");

		element.addClass("visible");

		setTimeout(() => {
			element.removeClass("visible");
		}, 2000);
	}
}
